import React from 'react';
import { Formik } from "formik";
import {
    Button,
    CircularProgress,
    Typography,
    withStyles,
    Checkbox,
    FormControlLabel,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    FormGroup
} from "@material-ui/core";

import * as Yup from 'yup';
import { types as fileTypes } from '../../sc-utilities/ExportFormUtilities';
import Divider from '@material-ui/core/Divider';

const styles = theme => ({
    root2: {
        flexGrow: 1,
        marginTop: theme.spacing(2),
        width: '80%',
        marginLeft: '3rem',
        marginRight: '3rem',
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "80%",
        marginTop: theme.spacing(5),
        bottom: 15,
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    formControl: {
        marginTop: theme.spacing(-3),
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(0.1),
        marginRight: theme.spacing(5),
        minWidth: 180,
    },
    formGroup: {
        marginTop: theme.spacing(-2),
        width: "100%",
    },
    dropdown: {
        width: "100%",
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    errorMsg: {
        textAlign: 'center',
        color: "red",
        fontWeight: 'bold'
    },
    exportFormat: {
        display: 'flex',
    }
});

const visibilities = [{ value: "both", displayValue: "Both" }, { value: "visible", displayValue: "Visible" }, { value: "hidden", displayValue: "Hidden" }];
const types = [{ value: "both", displayValue: "Both" }, { value: "system", displayValue: "System" }, { value: "custom", displayValue: "Custom" }];

const ExportForm = (props) => {
    const {
        initialValues,
        handleDownload,
        formSettings,
        classes,
        translations,
        showVisibilities,
        showTypes,
        showProductOverviewExportTypes,
        showProductOverviewOutputMode,
        preSelectAll,
        groups
    } = props;

    const obj = Object.values(
        initialValues.map(el => ({
            'name': el,
            'label': (el + '').includes('ID') ? el.replace('ID', 'Id').split(/(?=[A-Z])/).join(" ").replace(/\s/g, '_').toLowerCase() : (el + "").split(/(?=[A-Z])/).join(" ").replace(/\s/g, '_').toLowerCase() || el + ""
        }))
    );

    let objectOfDisplayValues = initialValues.reduce((acc, elem) => {
        acc[elem] = preSelectAll ? true : false
        acc['Excel'] = true
        acc['Download'] = true
        acc['Email'] = true
        return acc
    }, {});

    objectOfDisplayValues['generateNew'] = false;
    objectOfDisplayValues['individualExcelReports'] = false;
    objectOfDisplayValues['individualXmlReports'] = false;
    objectOfDisplayValues['individualJsonReports'] = false;
    objectOfDisplayValues['individualCsvReports'] = false;
    objectOfDisplayValues = {
        ...objectOfDisplayValues,
        FileType: "json",
        GroupID: groups?.map(group => group.value)?.includes('n/a') ? 'n/a' : groups?.[0]?.value ?? null
    }
    if (showVisibilities) {
        objectOfDisplayValues["Visibility"] = "both"
    }
    if (showTypes) {
        objectOfDisplayValues["Type"] = "both"
    }
    if (showProductOverviewOutputMode) {
        objectOfDisplayValues["OutputModes"] = ["download", "email"]
    }

    return (
        <Formik
            initialValues={objectOfDisplayValues}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                await handleDownload(values);
                setSubmitting(false);
            }}
            validationSchema={Yup.object(formSettings.Validation).test(
                'Checkbox validation',
                null,
                (obj) => {
                    const exportFormats = Object.keys(obj).filter((key) => ["Excel", "JSON", "XML", "CSV"].includes(key))
                        .map((value) => obj[value]).some(value => value);
                    const outputModes = Object.keys(obj).filter((key) => ["Download", "Email"].includes(key))
                        .map((value) => obj[value]).some(value => value);
                    if (showProductOverviewOutputMode) {
                        if (exportFormats && outputModes) {
                            return true;
                        }
                    } else if (exportFormats) {
                        return true;
                    }
                    return new Yup.ValidationError(
                        translations['checkbox_error'] || 'Please select at least one checkbox ',
                        null,
                        'Checkbox validation'
                    );
                }
            )}
        >
            {formikProps => {
                const {
                    values,
                    handleChange,
                    isValid,
                    errors,
                    touched,
                    isSubmitting,
                    handleBlur,
                    handleSubmit
                } = formikProps;

                return (
                    <div className={classes.root2}>
                        <form onSubmit={handleSubmit}>
                            {showProductOverviewOutputMode && <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["group"] || "Group"}</InputLabel>
                                <Select
                                    name="GroupID"
                                    label={translations["group"] || "Group"}
                                    margin={"dense"}
                                    value={values.GroupID || ''}
                                    onChange={handleChange}
                                >
                                    {(groups ? [...groups] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>}
                            {!showProductOverviewExportTypes && <FormControl className={classes.formControl}>
                                <InputLabel
                                    id="demo-simple-select-label">{translations['choose_type'] || `Choose file type`}</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    fullWidth={true}
                                    name='FileType'
                                    error={!!(errors.FileType && touched.FileType)}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values?.FileType || "json"}
                                >
                                    {fileTypes.map((el) => {
                                        return (
                                            <MenuItem key={el.value} value={el.value}>{el.displayValue}</MenuItem>)
                                    })}
                                </Select>
                            </FormControl>}
                            {(showVisibilities || showTypes) && <FormGroup className={classes.formGroup}>
                                {showVisibilities && <FormControl margin={"dense"}>
                                    <InputLabel
                                        shrink>{translations['visibility_dropdown'] || "Please select visibility"}</InputLabel>
                                    <Select
                                        name="Visibility"
                                        label={translations['visibility_dropdown'] || 'Please select visibility'}
                                        margin={"dense"}
                                        value={values?.Visibility?.toString() || ''}
                                        onChange={handleChange}
                                    >
                                        {(visibilities ? [...visibilities] : []).map(option => (
                                            <MenuItem key={option.value} value={option.value.toString()}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>}
                                {showTypes && <FormControl margin={"dense"}>
                                    <InputLabel
                                        shrink>{translations['export_data_type_dropdown'] || "Please select type"}</InputLabel>
                                    <Select
                                        name="Type"
                                        label={translations['export_data_type_dropdown'] || 'Please select type'}
                                        margin={"dense"}
                                        value={values?.Type?.toString() || ''}
                                        onChange={handleChange}
                                    >
                                        {(types ? [...types] : []).map(option => (
                                            <MenuItem key={option.value} value={option.value.toString()}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>}
                            </FormGroup>}
                            {obj.filter((el) => !["includePDFReports", "includeDiagnoseReports", "includeVerificationReports", "includeAggregatedPDFErasureReports", "includeAggregatedPDFVerificationReports", "includeSummaryPDFErasureReports", "includeSummaryPDFVerificationReports", "Download", "Email", "Excel", "JSON", "XML", "CSV"].includes(el.name)).map(el => {
                                return (
                                        <div key={el.name}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values[el.name]} key={el.name} onChange={handleChange}
                                                        name={el.name} />}
                                                label={el.label && translations[el.label] ? translations[el.label] : el.name} />
                                        </div>)
                            })}
                            {showProductOverviewOutputMode && <><InputLabel
                                shrink>{translations['select_report_overview_type'] || "Please select report overview type"}
                            </InputLabel>
                            <div className={classes.exportFormat}>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["Excel"]} key={"Excel"} onChange={handleChange}
                                                name={"Excel"} />}
                                        label={`Excel ${translations['file_format'] || 'file format'} (.xlsx)`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["JSON"]} key={"JSON"} onChange={handleChange}
                                                name={"JSON"} />}
                                        label={`JSON ${translations['file_format'] || 'file format'} (.json)`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["XML"]} key={"XML"} onChange={handleChange}
                                                name={"XML"} />}
                                        label={`XML ${translations['file_format'] || 'file format'} (.xml)`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["CSV"]} key={"CSV"} onChange={handleChange}
                                                name={"CSV"} />}
                                        label={`CSV ${translations['file_format'] || 'file format'} (.csv)`} />
                                </div>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["individualExcelReports"]} key={"individualExcelReports"} onChange={handleChange}
                                                name={"individualExcelReports"} disabled={!values["Excel"]} />}
                                        label={`${translations['one_xlsx_file_per_asset'] || 'One .xlsx file per asset'}`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["individualJsonReports"]} key={"individualJsonReports"} onChange={handleChange}
                                                name={"individualJsonReports"} disabled={!values["JSON"]} />}
                                        label={`${translations['one_json_file_per_asset'] || 'One .json file per asset'}`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["individualXmlReports"]} key={"individualXmlReports"} onChange={handleChange}
                                                name={"individualXmlReports"} disabled={!values["XML"]} />}
                                        label={`${translations['one_xml_file_per_asset'] || 'One .xml file per asset'}`} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values["individualCsvReports"]} key={"individualCsvReports"} onChange={handleChange}
                                                name={"individualCsvReports"} disabled={!values["CSV"]} />}
                                        label={`${translations['one_csv_file_per_asset'] || 'One .csv file per asset'}`} />
                                </div>
                            </div>
                            </>}
                            {showProductOverviewOutputMode && <FormGroup className={classes.dropdown} margin={"dense"}>
                                <InputLabel
                                    shrink>{translations['select_output_mode'] || "Please select output mode"}
                                </InputLabel>
                                {obj.map((el) => {
                                    if (el.label === "download" || el.label === "email") {
                                        return (
                                            <div key={el.name}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values[el.name]} key={el.name}
                                                            onChange={handleChange}
                                                            name={el.name} />}
                                                    label={translations[el.label] || el.name} />
                                            </div>)
                                    }
                                    return null
                                })}
                            </FormGroup>}
                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includePDFReports} key={"includePDFReports"}
                                        onChange={handleChange}
                                        name={"includePDFReports"} />}
                                label={translations["include_pdf_reports"] || "Include pdf erasure reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeDiagnoseReports} key={"includeDiagnoseReports"}
                                        onChange={handleChange}
                                        name={"includeDiagnoseReports"} />}
                                label={translations["include_diagnose_reports"] || "Include diagnose reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeVerificationReports} key={"includeVerificationReports"}
                                        onChange={handleChange}
                                        name={"includeVerificationReports"} />}
                                label={translations["include_verification_reports"] || "Include verification reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeAggregatedPDFErasureReports} key={"includeAggregatedPDFErasureReports"}
                                        onChange={handleChange}
                                        name={"includeAggregatedPDFErasureReports"} />}
                                label={translations["include_aggregated_pdf_erasure_reports"] || "Include aggregated pdf erasure reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeAggregatedPDFVerificationReports} key={"includeAggregatedPDFVerificationReports"}
                                        onChange={handleChange}
                                        name={"includeAggregatedPDFVerificationReports"} />}
                                label={translations["include_aggregated_pdf_verification_reports"] || "Include aggregated pdf verification reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeSummaryPDFErasureReports} key={"includeSummaryPDFErasureReports"}
                                        onChange={handleChange}
                                        name={"includeSummaryPDFErasureReports"} />}
                                label={translations["include_summary_pdf_erasure_reports"] || "Include summary pdf erasure reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.includeSummaryPDFVerificationReports} key={"includeSummaryPDFVerificationReports"}
                                        onChange={handleChange}
                                        name={"includeSummaryPDFVerificationReports"} />}
                                label={translations["include_summary_pdf_verification_reports"] || "Include summary pdf verification reports"} /></>}

                            {showProductOverviewOutputMode && <><Divider /><FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.generateNew} key={"generateNew"}
                                        onChange={handleChange}
                                        name={"generateNew"} />}
                                label={translations["generate_new_reports"] || "Generate new reports"} /></>}

                            {!isValid ? <Typography variant='subtitle1'
                                className={classes.errorMsg}> {translations['checkbox_error'] || 'Please select at least one checkbox'}</Typography> : null}
                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting || !isValid}
                                >
                                    {(isSubmitting) ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                </Button>
                            ) : null}

                        </form>
                    </div>
                );
            }}
        </Formik>
    )
}

export default (withStyles(styles, { withTheme: true })(ExportForm));
